export const MARKET_EXPLORER_CATEGORY_IDS = [
  "plcs_io",
  "drives_motion",
  "hmis",
  "sensors",
  "safety",
  "networking",
  "power",
  "process_instrumentation",
] as const;

export type MarketExplorerCategoryId = typeof MARKET_EXPLORER_CATEGORY_IDS[number];

export type MarketExplorerFacetKind =
  | "manufacturer"
  | "condition"
  | "supplier"
  | "max_price"
  | "voltage"
  | "power"
  | "protocol"
  | "channels"
  | "ports"
  | "poles"
  | "phases"
  | "ingress_protection"
  | "current"
  | "frequency";

export interface MarketExplorerCategory {
  id: MarketExplorerCategoryId;
  label: string;
  shortLabel: string;
  description: string;
  canonicalQuery: string;
  supplierQuery: string;
  manufacturers: readonly string[];
  manufacturerSearchTerms: readonly string[];
  productFamilies: readonly string[];
  marketplaceCategories: readonly string[];
  facets: readonly MarketExplorerFacetKind[];
}

const COMMON_FACETS = ["manufacturer", "condition", "supplier", "max_price"] as const;

export const MARKET_EXPLORER_CATEGORIES: readonly MarketExplorerCategory[] = [
  {
    id: "plcs_io",
    label: "PLCs & I/O",
    shortLabel: "PLC & I/O",
    description: "Controllers, CPUs, remote I/O and signal modules.",
    canonicalQuery: "PLCs & I/O",
    supplierQuery: "PLC CPUs and industrial I/O modules",
    manufacturers: [
      "Siemens", "Allen-Bradley", "Schneider Electric", "Mitsubishi Electric", "Omron", "ABB", "Beckhoff", "WAGO",
      "B&R", "Phoenix Contact", "Emerson", "Honeywell", "Bosch Rexroth", "Keyence", "Delta Electronics", "Panasonic",
      "GE Vernova", "Fuji Electric", "Hitachi", "IDEC", "LS Electric", "Unitronics", "Eaton", "Yokogawa",
    ],
    manufacturerSearchTerms: ["PLC CPU", "remote I/O", "digital I/O module", "analogue I/O module"],
    productFamilies: [
      "PLC", "PLC processor", "ET 200SP", "S7-1500", "S7-1200", "ControlLogix", "CompactLogix",
      "Remote I/O", "Digital output module", "Digital input module", "Analogue output module",
      "Analogue input module", "Analogue I/O module", "Temperature module", "Counter module", "I/O module",
    ],
    marketplaceCategories: ["plc_modules"],
    facets: [...COMMON_FACETS, "voltage", "protocol", "channels"],
  },
  {
    id: "drives_motion",
    label: "Drives & Motion",
    shortLabel: "Drives",
    description: "VFDs, servo drives, motors and motion controllers.",
    canonicalQuery: "Drives & Motion",
    supplierQuery: "industrial drives motors and motion control",
    manufacturers: [
      "Siemens", "ABB", "Schneider Electric", "Allen-Bradley", "Mitsubishi Electric", "Yaskawa", "Danfoss", "SEW-Eurodrive", "Lenze",
      "Omron", "Beckhoff", "Bosch Rexroth", "Kollmorgen", "Parker Hannifin", "Nidec Control Techniques", "Fuji Electric",
      "Delta Electronics", "Bonfiglioli", "KEB Automation", "Invertek Drives", "WEG", "B&R", "Baumuller", "KEBA", "LS Electric",
    ],
    manufacturerSearchTerms: ["variable frequency drive", "servo drive", "motion controller", "motor starter"],
    productFamilies: ["Motion controller", "Servo drive", "Servo motor", "Variable-frequency drive", "Soft starter", "Motor starter"],
    marketplaceCategories: ["drives", "motors", "actuators"],
    facets: [...COMMON_FACETS, "power", "voltage", "phases", "protocol", "frequency"],
  },
  {
    id: "hmis",
    label: "HMIs",
    shortLabel: "HMIs",
    description: "Operator panels, touchscreens and industrial computers.",
    canonicalQuery: "HMIs",
    supplierQuery: "industrial HMI operator panels and displays",
    manufacturers: [
      "Siemens", "Allen-Bradley", "Schneider Electric", "Mitsubishi Electric", "Omron", "Pro-face", "Beijer Electronics", "Weintek",
      "Red Lion", "Beckhoff", "B&R", "Advantech", "Delta Electronics", "Phoenix Contact", "WAGO", "EXOR International",
      "IDEC", "ESA Automation", "Maple Systems", "KEBA", "LS Electric",
    ],
    manufacturerSearchTerms: ["HMI operator panel", "touchscreen HMI", "industrial panel PC"],
    productFamilies: ["HMI", "Industrial PC"],
    marketplaceCategories: ["hmi_displays", "control_panels"],
    facets: [...COMMON_FACETS, "voltage", "protocol", "ports", "ingress_protection"],
  },
  {
    id: "sensors",
    label: "Sensors",
    shortLabel: "Sensors",
    description: "Industrial sensors, encoders and condition monitoring.",
    canonicalQuery: "Sensors",
    supplierQuery: "industrial sensors encoders and measurement devices",
    manufacturers: [
      "SICK", "ifm", "Pepperl+Fuchs", "Keyence", "Turck", "Balluff", "Omron", "Banner Engineering",
      "Baumer", "Leuze", "Contrinex", "Carlo Gavazzi", "Honeywell", "Wenglor", "Datalogic", "Micro-Epsilon",
      "Panasonic", "Telemecanique Sensors", "Rockwell Automation", "Pilz", "Festo", "Endress+Hauser", "VEGA", "KROHNE",
    ],
    manufacturerSearchTerms: ["industrial sensor", "encoder", "vibration sensor", "measurement sensor"],
    productFamilies: ["Sensor", "Encoder", "Vibration monitor", "Load cell"],
    marketplaceCategories: ["sensors"],
    facets: [...COMMON_FACETS, "voltage", "protocol", "ingress_protection", "current"],
  },
  {
    id: "safety",
    label: "Safety",
    shortLabel: "Safety",
    description: "Safety PLCs, relays and intrinsically safe equipment.",
    canonicalQuery: "Safety",
    supplierQuery: "industrial safety controllers relays and barriers",
    manufacturers: [
      "Pilz", "Siemens", "Allen-Bradley", "Schneider Electric", "SICK", "Omron", "Pepperl+Fuchs", "Eaton",
      "Banner Engineering", "Keyence", "Leuze", "Schmersal", "Euchner", "ABB Jokab Safety", "Phoenix Contact", "Turck",
      "ReeR", "Fortress Safety", "IDEC", "Datalogic", "Panasonic",
    ],
    manufacturerSearchTerms: ["safety controller", "safety relay", "intrinsic safety barrier"],
    productFamilies: ["Safety controller", "Safety relay", "Intrinsic safety barrier"],
    marketplaceCategories: ["safety_devices"],
    facets: [...COMMON_FACETS, "voltage", "channels", "protocol"],
  },
  {
    id: "networking",
    label: "Networking",
    shortLabel: "Networking",
    description: "Industrial switches, gateways and communications modules.",
    canonicalQuery: "Networking",
    supplierQuery: "industrial networking switches gateways and communication modules",
    manufacturers: [
      "Siemens", "Hirschmann", "Phoenix Contact", "Moxa", "HMS Networks Anybus", "Red Lion", "Beckhoff", "WAGO",
      "Rockwell Automation", "Schneider Electric", "Weidmuller", "Belden", "Cisco Industrial", "Advantech", "Westermo", "Antaira",
      "Turck", "Hilscher", "ProSoft Technology", "Contemporary Controls", "N-Tron", "B&R", "Emerson", "Kunbus",
    ],
    manufacturerSearchTerms: ["industrial ethernet switch", "industrial gateway", "communication module"],
    productFamilies: ["Ethernet module", "Communication module", "Industrial gateway", "Industrial switch"],
    marketplaceCategories: ["networking"],
    facets: [...COMMON_FACETS, "protocol", "ports", "voltage"],
  },
  {
    id: "power",
    label: "Power",
    shortLabel: "Power",
    description: "Power supplies, breakers, contactors and control relays.",
    canonicalQuery: "Power",
    supplierQuery: "industrial power supplies contactors breakers and relays",
    manufacturers: [
      "Phoenix Contact", "Siemens", "WAGO", "Schneider Electric", "ABB", "Eaton", "Omron", "Weidmuller",
      "PULS", "Mean Well", "TDK-Lambda", "Rockwell Automation", "Socomec", "Finder", "Carlo Gavazzi", "Mersen",
      "Lovato Electric", "SolaHD", "Delta Electronics", "IDEC", "CHINT", "Rittal", "Fuji Electric",
    ],
    manufacturerSearchTerms: ["industrial power supply", "contactor", "circuit breaker", "control relay"],
    productFamilies: ["Power supply", "Circuit breaker", "Contactor", "Industrial relay"],
    marketplaceCategories: ["power_supplies"],
    facets: [...COMMON_FACETS, "voltage", "current", "power", "poles", "phases"],
  },
  {
    id: "process_instrumentation",
    label: "Process Instrumentation",
    shortLabel: "Instrumentation",
    description: "Transmitters, flow, level, valves and process measurement.",
    canonicalQuery: "Process Instrumentation",
    supplierQuery: "process instrumentation transmitters flow level and control",
    manufacturers: [
      "Endress+Hauser", "Emerson", "Siemens", "ABB", "Yokogawa", "Honeywell", "VEGA", "KROHNE", "WIKA",
      "Rosemount", "Burkert", "Azbil", "Fuji Electric", "Schneider Electric", "Magnetrol", "ifm", "SICK",
      "Pepperl+Fuchs", "Hach", "Spirax Sarco", "SAMSON", "Fisher", "Badger Meter", "Brooks Instrument",
      "Bronkhorst", "Rotork", "Flowserve", "Metso Neles", "Dwyer Instruments", "Omega Engineering",
    ],
    manufacturerSearchTerms: ["pressure transmitter", "flow instrument", "level instrument", "process controller"],
    productFamilies: [
      "Pressure transmitter", "Temperature transmitter", "Flow instrument", "Level instrument", "Signal conditioner",
      "Process controller", "Valve positioner", "Control valve", "I/P converter", "Process analyser", "Recorder",
      "Calibration instrument",
    ],
    marketplaceCategories: ["sensors", "actuators"],
    facets: [...COMMON_FACETS, "protocol", "current", "voltage", "ingress_protection"],
  },
] as const;

const CATEGORY_BY_ID = new Map(MARKET_EXPLORER_CATEGORIES.map((category) => [category.id, category]));
const CATEGORY_BY_FAMILY = new Map(MARKET_EXPLORER_CATEGORIES.flatMap((category) => (
  category.productFamilies.map((family) => [family.toLocaleLowerCase("en-GB"), category.id] as const)
)));

export function isMarketExplorerCategoryId(value: unknown): value is MarketExplorerCategoryId {
  return typeof value === "string" && CATEGORY_BY_ID.has(value as MarketExplorerCategoryId);
}

export function getMarketExplorerCategory(value: unknown): MarketExplorerCategory | null {
  return isMarketExplorerCategoryId(value) ? CATEGORY_BY_ID.get(value) ?? null : null;
}

export function marketExplorerCategoryForProductFamily(productFamily: string | null | undefined): MarketExplorerCategoryId | null {
  return productFamily ? CATEGORY_BY_FAMILY.get(productFamily.toLocaleLowerCase("en-GB")) ?? null : null;
}

export function isMarketExplorerSeedQuery(query: string, categoryId: MarketExplorerCategoryId | null | undefined): boolean {
  const category = getMarketExplorerCategory(categoryId);
  const normalized = (value: string) => value.toLocaleLowerCase("en-GB").replace(/[^a-z0-9]+/g, " ").trim();
  return Boolean(category && [category.canonicalQuery, category.supplierQuery]
    .some((candidate) => normalized(query) === normalized(candidate)));
}
